import type {
  Column,
  ColumnDef,
  OnChangeFn,
  RowData,
  SortDirection,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';

import { cn } from '#/utils/cn';

import { Pagination } from '../ui/Pagination';

// Per-column display options, surfaced through TanStack's `meta` slot so column
// authors can tweak presentation without the table needing to know specifics.
declare module '@tanstack/react-table' {
  // Names must match the original declaration's type params (TS2428), even
  // though this augmentation doesn't reference them.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'start' | 'end';
    // When true, each data cell is prefixed with an sr-only copy of the column
    // header text so screen readers announce "<header>: <value>" for values that
    // would otherwise be read without their column context (e.g. bare numbers).
    announceHeader?: boolean;
  }
}

// Translate a column's optional sizing hints into inline styles. TanStack fills
// `size`/`minSize`/`maxSize` with defaults (150 / 20 / MAX_SAFE_INTEGER).
function getColumnSizeStyle<TData>(
  column: Column<TData, unknown>,
): CSSProperties | undefined {
  const { size, minSize, maxSize } = column.columnDef;
  if (size == null && minSize == null && maxSize == null) {
    return undefined;
  }
  return {
    width: size,
    minWidth: minSize,
    maxWidth: maxSize,
  };
}

// Resolve a column's header to plain text for use as an sr-only cell label.
// Only string headers can be announced this way; non-string (custom render)
// headers return undefined so we skip the prefix rather than announce markup.
function getHeaderLabel<TData>(
  column: Column<TData, unknown>,
): string | undefined {
  const header = column.columnDef.header;
  return typeof header === 'string' ? header : undefined;
}

// Build an action-describing accessible name for a sort button so non-visual
// users know both the current sort state and what activating it will do. The
// sort glyph itself is aria-hidden, so this label is the sole text alternative.
function getSortButtonLabel(
  headerLabel: string | undefined,
  sorted: SortDirection | false,
): string {
  const column = headerLabel ?? 'column';
  if (sorted === 'asc') {
    return `Sorted by ${column} ascending. Activate to sort descending.`;
  }
  if (sorted === 'desc') {
    return `Sorted by ${column} descending. Activate to sort ascending.`;
  }
  return `No sort applied to ${column}. Activate to sort ascending.`;
}

// Build the analytics label for a sort click. Unlike the aria-label (which
// describes the *current* state), this reports the state the click will produce
// — the column plus the direction it is about to sort by — so the emitted event
// matches what the user chose rather than the state before the click.
function getSortTrackLabel(
  headerLabel: string | undefined,
  nextSort: SortDirection | false,
): string {
  const column = headerLabel ?? 'column';
  if (nextSort === 'asc') {
    return `${column} ascending`;
  }
  if (nextSort === 'desc') {
    return `${column} descending`;
  }
  // `getNextSortingOrder()` returns false when the next click clears sorting
  // (only reachable when sorting removal is enabled).
  return `${column} unsorted`;
}

function getAriaSort(
  canSort: boolean,
  sorted: SortDirection | false,
): 'ascending' | 'descending' | 'none' | undefined {
  if (!canSort) {
    return undefined;
  }
  if (sorted === 'asc') {
    return 'ascending';
  }
  if (sorted === 'desc') {
    return 'descending';
  }
  return 'none';
}

export interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  /** Default sort, ex. `[{ id: 'current_shares', desc: true }]`. */
  initialSorting?: SortingState;
  /** Controlled sorting state for manual or external sorting flows. */
  sorting?: SortingState;
  /** Called when the sorting state changes. */
  onSortingChange?: OnChangeFn<SortingState>;
  /** When true, allows the user to remove sorting by clicking the column header multiple times. */
  enableSortingRemoval?: boolean;
  /** When true, preserve sort UI/state but skip client-side row reordering. */
  manualSorting?: boolean;
  /** When set, the table paginates client-side. */
  pageSize?: number;
  getRowId?: (row: TData, index: number) => string;
  className?: string;
  compact?: boolean;
  /** Accessible name for the table, so it is never left nameless to assistive tech. */
  'aria-label'?: string;
  /** Rendered in place of rows when `data` is empty. Defaults to "No results". */
  emptyState?: ReactNode;
  /**
   * Analytics grouping bucket (`data-track-context`) for the sortable column
   * headers. This table is a shared primitive rendered on several pages/
   * sections, so the parent passes a context (e.g. `"explore_table"`) that
   * tells the analytics listener which section the sort came from. When unset,
   * the sort buttons carry no `data-track*` and are captured generically.
   */
  trackContext?: string;
}

function SortIndicator({ direction }: { direction: SortDirection | false }) {
  const isASC = direction === 'asc';
  const isDESC = direction === 'desc';

  return (
    <svg
      className="h-12 w-auto shrink-0"
      viewBox="0 0 9 12"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        className={cn({
          'text-slate-90': isDESC,
          'text-slate-50': !isDESC,
        })}
        d={
          isDESC
            ? 'M0.0557,7.2141C0.1729,6.9328 0.4448,6.75 0.7495,6.75L8.2495,6.75C8.5518,6.75 8.826,6.9328 8.9432,7.2141C9.0604,7.4953 8.9948,7.8164 8.7815,8.032L5.0315,11.782C4.7385,12.075 4.2628,12.075 3.9698,11.782L0.2198,8.032C0.0042,7.8164 -0.0591,7.4953 0.0581,7.2141L0.0557,7.2141Z'
            : 'M1.6542,7.875L4.4995,10.7203L7.3448,7.875L1.6542,7.875ZM0.0557,7.2141C0.1729,6.9328 0.4448,6.75 0.7495,6.75L8.2495,6.75C8.5518,6.75 8.826,6.9328 8.9432,7.2141C9.0604,7.4953 8.9948,7.8164 8.7815,8.032L5.0315,11.782C4.7385,12.075 4.2628,12.075 3.9698,11.782L0.2198,8.032C0.0042,7.8164 -0.0591,7.4953 0.0581,7.2141L0.0557,7.2141Z'
        }
        fill="currentColor"
      />
      <path
        className={cn({
          'text-slate-90': isASC,
          'text-slate-50': !isASC,
        })}
        d={
          isASC
            ? 'M0.0565,4.7862C0.1736,5.0675 0.4455,5.2503 0.7502,5.2503L8.2502,5.2503C8.5526,5.2503 8.8268,5.0675 8.944,4.7862C9.0611,4.505 8.9955,4.1839 8.7822,3.9683L5.0322,0.2183C4.7393,-0.0747 4.2635,-0.0747 3.9705,0.2183L0.2205,3.9683C0.0049,4.1839 -0.0584,4.505 0.0588,4.7862L0.0565,4.7862Z'
            : 'M1.6549,4.1253L4.5002,1.28L7.3455,4.1253L1.6549,4.1253ZM0.0565,4.7862C0.1736,5.0675 0.4455,5.2503 0.7502,5.2503L8.2502,5.2503C8.5526,5.2503 8.8268,5.0675 8.944,4.7862C9.0611,4.505 8.9955,4.1839 8.7822,3.9683L5.0322,0.2183C4.7393,-0.0747 4.2635,-0.0747 3.9705,0.2183L0.2205,3.9683C0.0049,4.1839 -0.0584,4.505 0.0588,4.7862L0.0565,4.7862Z'
        }
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Generic TanStack Table with sortable column headers.
 * Sorting is opt-in per column via `enableSorting`.
 * Pagination is opt-in via `pageSize`.
 * Column widths are opt-in per column via `size` / `minSize` / `maxSize` on the
 * column def; columns without them keep the table's automatic sizing.
 */
export function DataTable<TData>({
  columns,
  data,
  initialSorting = [],
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  enableSortingRemoval = false,
  pageSize,
  getRowId,
  className,
  compact = false,
  'aria-label': ariaLabel,
  emptyState = 'No results',
  trackContext,
}: DataTableProps<TData>) {
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(initialSorting);
  const sorting = controlledSorting ?? internalSorting;
  const paginated = pageSize != null;

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (controlledSorting == null) {
      setInternalSorting(updater);
    }

    onSortingChange?.(updater);
  };

  const table = useReactTable({
    data,
    columns,
    getRowId,
    manualSorting,
    state: { sorting },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval,
    ...(!manualSorting && {
      getSortedRowModel: getSortedRowModel(),
    }),
    ...(paginated && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: { pagination: { pageSize } },
    }),
  });

  return (
    <div className={cn('flow flow-20 data-table', className)}>
      <div className="overflow-x-shadow overflow-x-auto">
        <table
          aria-label={ariaLabel}
          className={cn(
            'text-table-fg w-full border-collapse',
            compact
              ? 'text-static-12 min-w-0 table-fixed'
              : 'text-static-14 min-w-lg',
          )}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-divider-subtle border-b"
              >
                {headerGroup.headers.map((header) => {
                  const { align = 'start' } =
                    header.column.columnDef.meta ?? {};
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const label = flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  );
                  const headerLabel = getHeaderLabel(header.column);
                  const sortButtonLabel = getSortButtonLabel(
                    headerLabel,
                    sorted,
                  );
                  // The direction the next click will apply, so the analytics
                  // label reflects the user's intended target rather than the
                  // current (pre-click) state.
                  const sortTrackLabel = getSortTrackLabel(
                    headerLabel,
                    header.column.getNextSortingOrder(),
                  );

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={getAriaSort(canSort, sorted)}
                      className={cn(
                        'text-static-14 leading-snug font-semibold text-pretty',
                        align === 'end' ? 'text-end' : 'text-start',
                        compact ? 'p-8' : 'p-12 first:ps-16 last:pe-16',
                      )}
                      style={getColumnSizeStyle(header.column)}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          aria-label={sortButtonLabel}
                          onClick={header.column.getToggleSortingHandler()}
                          data-track={
                            trackContext ? 'explore_table_sort' : undefined
                          }
                          data-track-label={
                            trackContext ? sortTrackLabel : undefined
                          }
                          data-track-context={trackContext}
                          className={cn(
                            'group hover:text-table-fg-hover inline-flex items-center gap-8',
                            align === 'end'
                              ? 'flex-row-reverse text-right'
                              : 'text-left',
                          )}
                        >
                          <span className="w-min">{label}</span>
                          <SortIndicator direction={sorted} />
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-divider-subtle divide-y">
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={table.getVisibleLeafColumns().length}
                  className={cn(
                    'text-fg-utility text-center',
                    compact ? 'px-8 py-12' : 'px-12 py-24',
                  )}
                >
                  {emptyState}
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const { align = 'start', announceHeader = true } =
                    cell.column.columnDef.meta ?? {};
                  const headerLabel = announceHeader
                    ? getHeaderLabel(cell.column)
                    : undefined;
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        'align-middle',
                        align === 'end' ? 'text-end' : 'text-start',
                        compact ? 'px-8 py-12' : 'p-12 first:ps-16 last:pe-16',
                      )}
                      style={getColumnSizeStyle(cell.column)}
                    >
                      {headerLabel && (
                        <span className="sr-only">{headerLabel}: </span>
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginated && table.getPageCount() > 1 && (
        <Pagination
          page={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          onPageChange={table.setPageIndex}
        />
      )}
    </div>
  );
}
